import KDBush from "kdbush";
import * as geokdbush from "geokdbush";
import haversineDistance from "haversine-distance";
import { Plottable, RiskCluster } from "./types"; // Ensure Plottable has lat, lon
import * as V from "./vector"; // Your vector math utilities

export type Coord = Parameters<typeof haversineDistance>[0];

export const MILES_TO_METERS = 1609.34;
export const MILES_TO_KM = MILES_TO_METERS / 1000;

export const formatMiles = (miles: number): string => {
  const factor = miles < 10 ? 10.0 : miles < 1000.0 ? 1.0 : 0.1;
  const roundedMiles = Math.round(miles * factor) / factor;
  return roundedMiles.toString();
};

export const haversineDistanceMiles = (a: Coord, b: Coord): number => {
  const meters = haversineDistance(a, b);
  return meters * (1 / MILES_TO_METERS);
};

const centerCluster = (a: RiskCluster): RiskCluster => {
  // adjust the centers for visual purposes
  const maxLat = a.included.reduce((a, b) => Math.max(a, b.lat), -Infinity);
  const minLat = a.included.reduce((a, b) => Math.min(a, b.lat), Infinity);
  const maxLon = a.included.reduce((a, b) => Math.max(a, b.lon), -Infinity);
  const minLon = a.included.reduce((a, b) => Math.min(a, b.lon), Infinity);
  const proposedLat = (maxLat + minLat) / 2;
  const proposedLon = (maxLon + minLon) / 2;

  // Binary search to find the position closest to proposed that still contains all the points.
  let i = 0.5;
  for (let dec = 0.25; dec >= 0.001; dec /= 2.0) {
    const newCenter = V.interp([a.lon, a.lat], [proposedLon, proposedLat], i);

    if (
      a.included.every((o) => haversineDistanceMiles(o, newCenter) <= a.radius)
    ) {
      i += dec;
    } else {
      i -= dec;
    }
  }

  const [lon, lat] = V.interp([a.lon, a.lat], [proposedLon, proposedLat], i);
  return {
    ...a,
    lat,
    lon,
  };
};

export const addressListSizeSupportsClusterAnalysis = (size: number) => {
  // This constant is selected so that:
  // 1. Almost any submission will render alongside bound locations (BSP has about 700 bound, UIP about 2500)
  // 2. We don't crash the browser if someone loads something with 29K locations.
  return size <= 3000;
};

export const findAddressesInRadius = (
  addresses: Plottable[],
  radiiMiles: { [key: string]: number },
  // Optional filter applied *during* cluster generation (e.g., basic validity)
  preFilter: (cluster: RiskCluster) => boolean = () => true
): RiskCluster[] => {
  if (!addressListSizeSupportsClusterAnalysis(addresses.length)) {
    return [];
  }
  if (Object.keys(radiiMiles).length === 0 || addresses.length === 0) {
    return [];
  }

  const pairwiseDistances: number[][] = Array.from(
    { length: addresses.length },
    () => Array(addresses.length).fill(0)
  );

  const distance = (i: number, j: number): number => {
    const cached = pairwiseDistances[i]![j]!;
    if (cached > 0) {
      return cached;
    }
    return (pairwiseDistances[i]![j]! = pairwiseDistances[j]![i]! =
      haversineDistanceMiles(addresses[i]!, addresses[j]!));
  };

  const index = new KDBush(addresses.length);
  for (let i = 0; i < addresses.length; i++) {
    const { lat, lon } = addresses[i]!;
    const id = index.add(lon, lat);
    if (i !== id) {
      throw new Error(`${i} !== ${id}`);
    }
  }
  index.finish();

  const potentialClusters: RiskCluster[] = [];
  const maxRadius = Math.max(...Object.values(radiiMiles));

  // --- Iterate through pairs to find potential centers (O(n^2)) ---
  for (let i = 0; i < addresses.length; i++) {
    const a = addresses[i]!;

    const bis: number[] = geokdbush.around(
      index,
      a.lon,
      a.lat,
      Infinity,
      2.0 * maxRadius * MILES_TO_KM * 1.01
    );

    for (const bi of bis) {
      if (bi <= i) {
        continue;
      }
      const b = addresses[bi]!;
      const dist = distance(i, bi);
      const mid: [number, number] = [(a.lon + b.lon) / 2, (a.lat + b.lat) / 2];
      const conversions = V.mileDegreeConversions(mid[1]);
      const vec: [number, number] = [
        (b.lon - a.lon) * conversions.degreesToMilesLon,
        (b.lat - a.lat) * conversions.degreesToMilesLat,
      ];
      const unitVec = V.unit(vec);
      const [norm1, norm2] = V.perpendiculars(unitVec);

      for (const [clusterType, radiusMiles] of Object.entries(radiiMiles)) {
        if (dist <= 2.0 * radiusMiles) {
          const distanceToCenterAlongNormMiles = Math.sqrt(
            radiusMiles * radiusMiles - (dist / 2) * (dist / 2)
          );

          const centerOffset1: [number, number] = [
            distanceToCenterAlongNormMiles *
              conversions.milesToDegreeLon *
              norm1[0],
            distanceToCenterAlongNormMiles *
              conversions.milesToDegreeLat *
              norm1[1],
          ];
          const centerOffset2: [number, number] = [
            distanceToCenterAlongNormMiles *
              conversions.milesToDegreeLon *
              norm2[0],
            distanceToCenterAlongNormMiles *
              conversions.milesToDegreeLat *
              norm2[1],
          ];

          const center1 = V.add(centerOffset1, mid);
          const center2 = V.add(centerOffset2, mid);

          for (const center of [center1, center2]) {
            const included = bis
              .map((i) => addresses[i])
              .filter(
                (address): address is Plottable =>
                  !!address &&
                  haversineDistanceMiles(address, center) <= radiusMiles * 1.01
              );

            if (included.length > 0) {
              const tiv = included.reduce((acc, item) => acc + item.tiv, 0.0);
              const cluster = {
                lon: center[0],
                lat: center[1],
                radius: radiusMiles,
                included: included,
                clusterType,
                tiv,
              };

              if (preFilter(cluster)) {
                const centered = centerCluster(cluster);
                const included = (
                  geokdbush.around(
                    index,
                    centered.lon,
                    centered.lat,
                    Infinity,
                    radiusMiles * MILES_TO_KM
                  ) as number[]
                )
                  .map((i) => addresses[i])
                  .filter((address): address is Plottable => !!address);
                const tiv = included.reduce((acc, item) => acc + item.tiv, 0.0);
                potentialClusters.push({
                  ...centered,
                  included,
                  tiv,
                });
              }
            }
          }
        }
      }
    }
  }

  potentialClusters.sort((a, b) => b.tiv - a.tiv);
  const accepted: RiskCluster[] = [];
  // Map: clusterType -> Set of points included in accepted clusters of that type
  const includedPointsByType = new Map<string, Set<Plottable>>();

  for (const cluster of potentialClusters) {
    const pointsInThisType = includedPointsByType.get(cluster.clusterType);
    const pointAlreadyIncludedInSameType = cluster.included.some((p) =>
      pointsInThisType?.has(p)
    );

    // If no point in this cluster conflicts with already accepted clusters *of the same type*
    if (!pointAlreadyIncludedInSameType) {
      accepted.push(cluster);

      // Update the set for this clusterType
      let currentSet = includedPointsByType.get(cluster.clusterType);
      if (!currentSet) {
        currentSet = new Set<Plottable>();
        includedPointsByType.set(cluster.clusterType, currentSet);
      }

      cluster.included.forEach((p) => currentSet.add(p));
    }
  }

  // --- Add Singleton Clusters ---
  // Singletons should only be added if a point is not part of *any* accepted cluster, regardless of type.
  // We need a combined set of all included points for this check.
  const allIncludedPoints = new Set<Plottable>();
  includedPointsByType.forEach((pointSet) => {
    pointSet.forEach((p) => allIncludedPoints.add(p));
  });

  for (const address of addresses) {
    if (!allIncludedPoints.has(address)) {
      const singletonCluster: RiskCluster = {
        lat: address.lat,
        lon: address.lon,
        tiv: address.tiv,
        radius: 0.25,
        included: [address],
        clusterType: "singleton",
      };
      if (preFilter(singletonCluster)) {
        accepted.push(singletonCluster);
      }
    }
  }

  return accepted;
};

type BBox = { lat: [number, number]; lon: [number, number] };

export const boundingBoxFromPoints = (xs: Plottable[]): BBox => {
  if (xs.length === 0) {
    // Just return something if we don't have any plottables (these coords are somewhere in the us)
    return { lat: [33.22111, -97.550874], lon: [37.160851, -93.213162] };
  }
  let minLat = Infinity;
  let minLon = Infinity;
  let maxLat = -Infinity;
  let maxLon = -Infinity;
  for (const x of xs) {
    minLat = Math.min(minLat, x.lat);
    minLon = Math.min(minLon, x.lon);
    maxLat = Math.max(maxLat, x.lat);
    maxLon = Math.max(maxLon, x.lon);
  }

  return {
    lat: [minLat, maxLat],
    lon: [minLon, maxLon],
  };
};

export const expandLatLonBoundingBoxByMiles = (
  { lat: [minLat, maxLat], lon: [minLon, maxLon] }: BBox,
  miles: number
): BBox => {
  // Taking the maximum of the conversion at the bounds is safe because the
  // conversions increase toward the poles and decrease toward the equator,
  // meaning we can never have a maxima in the middle of the bounds
  const conversions1 = V.mileDegreeConversions(minLat);
  const conversions2 = V.mileDegreeConversions(maxLat);
  const latBuffer = Math.max(
    conversions1.milesToDegreeLat * miles,
    conversions2.milesToDegreeLat * miles
  );
  const lonBuffer = Math.max(
    conversions1.milesToDegreeLon * miles,
    conversions2.milesToDegreeLon * miles
  );

  return {
    lat: [minLat - latBuffer, maxLat + latBuffer],
    lon: [minLon - lonBuffer, maxLon + lonBuffer],
  };
};

export const parseCoverage = (
  val: number | string | null | undefined
): number => {
  if (!val) return 0;
  const f = typeof val === "number" ? val : parseFloat(val);
  return !isNaN(f) ? f : 0;
};
